/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

// Import the store and context
import { useSearchResultsStore, SearchResultDataType } from '../../modules/store/searchResultsStore';
import { SearchResultContext } from '../../organisms/OSearchResults/index.context';
import { SearchActions } from '../../organisms/OSearchResults/store/store.action';

/**
 * Interface for SearchResultContext type - matching the Context API
 */
export interface SearchResultContextType {
  searchResult: SearchResultDataType;
  setSearchResult: (action: { type: SearchActions; payload: any }) => void;
}

/**
 * Extended interface when using Zustand - includes direct methods
 */
export interface SearchResultZustandType extends SearchResultContextType {
  setSearchResultData?: (payload: Partial<SearchResultDataType>) => void;
  changeCurrentView?: (showMapView: boolean) => void;
  updateMediaData?: (media: any) => void;
  resetSearchResult?: () => void;
}

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 *
 * Uses feature flags: EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND and EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND
 *
 * Note: The Zustand store is updated proactively in searchResultsMainComponent where
 * the SearchResultContext.Provider is set up, rather than reactively in this hook.
 * This is more efficient and follows the principle of updating at the source.
 */
export const useConditionalSearchResultContext = (flagType: 'adf' | 'serp' = 'serp'): SearchResultZustandType => {
  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Always call hooks at top level (Rules of Hooks)
  const contextValue = useContext(SearchResultContext);

  // Only subscribe to STATE using a selector (for reactivity)
  const searchResultData = useSearchResultsStore(state => state.searchResultData);

  // Get methods using selectors (methods are stable, won't cause re-renders)
  const setSearchResultData = useSearchResultsStore(state => state.setSearchResultData);
  const changeCurrentView = useSearchResultsStore(state => state.changeCurrentView);
  const updateMediaData = useSearchResultsStore(state => state.updateMediaData);
  const resetSearchResult = useSearchResultsStore(state => state.resetSearchResult);

  if (shouldUseZustand) {
    // Create a wrapper for setSearchResult that maintains backward compatibility
    // This allows existing code using dispatch pattern to continue working
    const setSearchResult = (action: { type: SearchActions; payload: any }) => {
      switch (action.type) {
        case SearchActions.setSearchResultData:
          setSearchResultData(action.payload);
          break;
        case SearchActions.changeCurrentView:
          changeCurrentView(action.payload.showMapView);
          break;
        case SearchActions.updateMedia:
          updateMediaData(action.payload.media);
          break;
        default:
          // For any unknown actions, use setSearchResultData as fallback
          setSearchResultData(action.payload);
      }
    };

    // Return Zustand store data with all direct methods
    return {
      searchResult: searchResultData, // Reactive state from selector
      setSearchResult, // Backward compatible dispatcher
      setSearchResultData,
      changeCurrentView,
      updateMediaData,
      resetSearchResult,
    };
  } else {
    // Return React Context data (only has searchResult and setSearchResult)
    return contextValue;
  }
};

/**
 * Convenience hook specifically for components that should use the ADF feature flag
 * (AvailabilityCalendar and CalendarWrapper components)
 *
 * This hook checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for SearchResultContext
 */
export const useConditionalSearchResultContextWithAdfFlag = (): SearchResultZustandType => {
  return useConditionalSearchResultContext('adf');
};

/**
 * Convenience hook specifically for components that should use the SERP feature flag
 * (Most components in the SearchResults ecosystem)
 *
 * This hook checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for SearchResultContext
 */
export const useConditionalSearchResultContextSERP = (): SearchResultZustandType => {
  return useConditionalSearchResultContext('serp');
};
